// V2 Campaign detail — matches 2d mock. Stat cards + funnel + People/Messages tabs.
// Reuses /api/campaigns/:id and /api/leads?campaignId. Read-only view for now;
// Pause / edit / delete affordances stay minimal and defer heavier campaign editing
// to the v1 CampaignBuilder if the coach needs it.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Anchor,
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPlus, IconTrash, IconUpload } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  fetchCampaign,
  fetchLeads,
  findMoreProspects,
  importLeadsFromCsv,
  patchCampaign,
} from '../services/api';

// Target size for the active prospect pool. Once we exceed this, the top-up bar
// reads "caught up". Hardcoded per Sunny's call — flip to per-campaign later.
const POOL_TARGET = 25;
const DEFAULT_MAX_TOUCHES = 6;
const DEFAULT_PER_EPISODE_BUDGET = 10;
const DEFAULT_SILENCE_DAYS = 14;
const DEFAULT_EXPLORE_SHARE = 0.3;
const DEFAULT_REPLY_CONFIDENCE_THRESHOLD = 0.8;
const VALID_WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

// Fold lead stages into the four funnel buckets we display.
function bucketOf(stage) {
  const s = String(stage || 'prospect').toLowerCase();
  if (s === 'contacted' || s === 'qualified') return 'contacted';
  if (s === 'clicked' || s === 'hot') return 'clicked';
  if (s === 'booked' || s === 'closed') return 'booked';
  if (s === 'replied') return 'replied';
  return 'prospect';
}

function StatCard({ label, value }) {
  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{ background: 'var(--aviary-surface)', flex: 1, minWidth: 0 }}
    >
      <Stack gap={2}>
        <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28, lineHeight: 1 }}>
          {value}
        </Text>
        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{label}</Text>
      </Stack>
    </Paper>
  );
}

// Visual funnel: 4 blocks scaled by count. Tallest bar = MAX_BAR px; empty stages
// show a thin baseline so the funnel silhouette stays visible.
function Funnel({ counts }) {
  const stages = [
    { key: 'prospect', label: 'Prospect' },
    { key: 'contacted', label: 'Contacted' },
    { key: 'clicked', label: 'Clicked' },
    { key: 'booked', label: 'Booked' },
  ];
  const MAX_BAR = 140;
  const EMPTY_BAR = 10;
  const max = Math.max(1, ...stages.map((s) => counts[s.key] || 0));
  const shades = ['sage-1', 'sage-3', 'sage-5', 'sage-7'];
  return (
    <Paper p="lg" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
      <Stack gap="md">
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
          FUNNEL
        </Text>
        <Group gap="md" align="flex-end" wrap="nowrap" justify="space-between">
          {stages.map((stage, i) => {
            const n = counts[stage.key] || 0;
            const barHeight = n === 0 ? EMPTY_BAR : Math.max(24, (n / max) * MAX_BAR);
            return (
              <Stack key={stage.key} gap={6} align="center" style={{ flex: 1 }}>
                <Box
                  style={{
                    width: '100%',
                    height: barHeight,
                    borderRadius: 6,
                    background: `var(--mantine-color-${shades[i]})`,
                  }}
                />
                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                  {stage.label} {n}
                </Text>
              </Stack>
            );
          })}
        </Group>
      </Stack>
    </Paper>
  );
}

function stageDisplay(stage) {
  const s = String(stage || '').toLowerCase();
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function stageColor(stage) {
  const s = String(stage || '').toLowerCase();
  if (s === 'clicked' || s === 'hot') return 'var(--mantine-color-sage-7)';
  if (s === 'booked' || s === 'closed') return 'var(--mantine-color-sage-7)';
  if (s === 'contacted' || s === 'qualified') return 'var(--aviary-ink)';
  return 'var(--aviary-muted)';
}

function formatAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diff = Date.now() - then;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hrs = Math.floor(diff / (60 * 60 * 1000));
  if (days > 0) return `${days}d`;
  if (hrs > 0) return `${hrs}h`;
  return 'just now';
}

// `lead.lastTouchAt` is initialised to `now` at import time in createLeadDoc, so it lies
// for prospects that have never actually been contacted. We only trust it once the lead
// has moved past 'prospect' — otherwise show "not contacted yet" and, if we know when the
// lead was added, an "added Xd ago" note.
function lastTouchLine(lead) {
  const stage = String(lead.stage || '').toLowerCase();
  if (!stage || stage === 'prospect') {
    const added = formatAgo(lead.createdAt);
    return added ? `— · added ${added} ago` : '—';
  }
  const iso = lead.lastTouchAt || lead.updatedAt;
  const when = formatAgo(iso);
  if (!when) return '—';
  const channel = lead.preferredChannel || '';
  const channelLabel = channel === 'linkedin' ? 'LinkedIn' : channel === 'email' ? 'Email' : channel === 'whatsapp' ? 'WhatsApp' : channel;
  return channelLabel ? `${channelLabel} · ${when}` : when;
}

function fitScoreOf(lead) {
  // Use only the AI fit score (0-100). Readiness score (lead.score, 1-5) is unrelated.
  const s = typeof lead.fitScore === 'number' && lead.fitScore >= 10 ? lead.fitScore : 0;
  return s > 0 ? Math.round(s) : null;
}

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PEOPLE_INITIAL = 15;

function PeopleTable({ leads, onOpenLead }) {
  const [showAll, setShowAll] = useState(false);
  if (!leads.length) {
    return (
      <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
        <Text ta="center" style={{ color: 'var(--aviary-muted)' }}>
          No people yet on this campaign.
        </Text>
      </Paper>
    );
  }
  const shown = showAll ? leads : leads.slice(0, PEOPLE_INITIAL);
  const hidden = leads.length - shown.length;

  return (
    <Paper p={0} radius="md" withBorder style={{ background: 'var(--aviary-surface)', overflow: 'hidden' }}>
      <Table verticalSpacing="sm" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ color: 'var(--aviary-muted)', fontWeight: 600, fontSize: 12, letterSpacing: 0.4 }}>PERSON</Table.Th>
            <Table.Th style={{ color: 'var(--aviary-muted)', fontWeight: 600, fontSize: 12, letterSpacing: 0.4 }}>STAGE</Table.Th>
            <Table.Th style={{ color: 'var(--aviary-muted)', fontWeight: 600, fontSize: 12, letterSpacing: 0.4 }}>LAST TOUCH</Table.Th>
            <Table.Th style={{ color: 'var(--aviary-muted)', fontWeight: 600, fontSize: 12, letterSpacing: 0.4, textAlign: 'right' }}>FIT</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.map((lead) => {
            const fit = fitScoreOf(lead);
            return (
              <Table.Tr
                key={lead.id}
                style={{ cursor: 'pointer' }}
                onClick={() => onOpenLead(lead.id)}
              >
                <Table.Td>
                  <Group gap="sm" wrap="nowrap" align="center">
                    <Avatar
                      src={lead.photoUrl || null}
                      size={32}
                      radius="xl"
                      color="sage"
                    >
                      {initialsFor(lead.name)}
                    </Avatar>
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" style={{ color: 'var(--aviary-ink)' }} truncate>
                        {lead.name || '(no name)'}
                      </Text>
                      {lead.company && (
                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }} truncate>
                          {lead.company}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ color: stageColor(lead.stage) }}>
                    {stageDisplay(lead.stage)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                    {lastTouchLine(lead)}
                  </Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                    {fit ?? '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {hidden > 0 && (
        <UnstyledButton
          onClick={() => setShowAll(true)}
          style={{ display: 'block', width: '100%', padding: 14, borderTop: '1px dashed var(--aviary-disabled)' }}
        >
          <Text size="sm" ta="center" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
            {hidden} more…
          </Text>
        </UnstyledButton>
      )}
    </Paper>
  );
}

function MessagesTab({ touches }) {
  if (!touches.length) {
    return (
      <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
        <Text ta="center" style={{ color: 'var(--aviary-muted)' }}>
          No messages drafted for this campaign yet.
        </Text>
      </Paper>
    );
  }
  return (
    <Stack gap="md">
      {touches.map((touch, index) => {
        const body = String(touch.messageTemplate || '').trim();
        const day = Number(touch.sendDayOffset || 0);
        return (
          <Paper key={touch.id || index} p="md" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Stack gap="xs">
              <Group gap="sm" align="baseline">
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                  {index === 0 ? 'OPENER' : `FOLLOW-UP #${index + 1}`}
                </Text>
                {index > 0 && day > 0 && (
                  <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                    · after {day} {day === 1 ? 'day' : 'days'}
                  </Text>
                )}
              </Group>
              {body ? (
                <Text
                  size="sm"
                  style={{
                    color: 'var(--aviary-ink)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.55,
                  }}
                >
                  {body}
                </Text>
              ) : (
                <Text size="sm" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                  Not drafted yet.
                </Text>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default function AviaryV2CampaignDetailPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: campaignId } = useParams();
  // Where "back" goes. Entry points pass state.back (e.g., board card → /v2/board).
  // Fresh URL fallback → campaigns list.
  const back = location.state?.back || { path: '/v2/campaigns', label: 'Campaigns' };

  const [campaign, setCampaign] = useState(null);
  const [touches, setTouches] = useState([]);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('people');
  const [pausing, setPausing] = useState(false);
  const [autonomySaving, setAutonomySaving] = useState(false);
  const [strategySaving, setStrategySaving] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Find-more loop state: idle | sourcing → then ready is derived from campaign.proposedProspects.
  const [sourcing, setSourcing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, leadData] = await Promise.all([
        fetchCampaign(api, campaignId),
        fetchLeads(api, { campaignId }),
      ]);
      setCampaign(detail?.campaign || null);
      setTouches(Array.isArray(detail?.touches) ? detail.touches : []);
      const list = Array.isArray(leadData) ? leadData : (leadData?.leads || []);
      setLeads(list);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api, campaignId]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const base = { prospect: 0, contacted: 0, clicked: 0, booked: 0, replied: 0 };
    for (const lead of leads) {
      const b = bucketOf(lead.stage);
      if (base[b] !== undefined) base[b] += 1;
      if (Array.isArray(lead.events) && lead.events.some((e) => e.type === 'replied')) {
        base.replied += 1;
      }
    }
    return base;
  }, [leads]);

  const openRate = useMemo(() => {
    const contacted = counts.contacted + counts.clicked + counts.booked;
    if (contacted === 0) return '0%';
    // Rough proxy: clicked count / contacted-or-past
    return `${Math.round((counts.clicked / contacted) * 100)}%`;
  }, [counts]);

  async function togglePause() {
    if (!campaign) return;
    setPausing(true);
    const nextPaused = campaign.paused !== true;
    try {
      const updated = await patchCampaign(api, campaign.id, { paused: nextPaused });
      setCampaign((prev) => ({ ...prev, ...updated, paused: updated?.paused === true }));
      notifications.show({
        title: nextPaused ? 'Paused' : 'Resumed',
        message: nextPaused ? 'Paused — resume anytime.' : 'Campaign resumed.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not update campaign',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setPausing(false);
    }
  }

  async function updateAutonomy(nextAutonomy) {
    if (!campaign || nextAutonomy === (campaign.autonomy || 'manual')) return;
    setAutonomySaving(true);
    try {
      const updated = await patchCampaign(api, campaign.id, { autonomy: nextAutonomy });
      setCampaign((prev) => ({ ...prev, ...updated, autonomy: updated?.autonomy || nextAutonomy }));
      notifications.show({
        title: 'Autonomy updated',
        message: nextAutonomy === 'blackbox' ? 'Blackbox mode enabled.' : 'Manual mode enabled.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not update autonomy',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setAutonomySaving(false);
    }
  }

  async function patchStrategyField(field, value) {
    if (!campaign) return;
    const previousCampaign = campaign;
    const optimistic = { ...campaign, [field]: value };
    setCampaign(optimistic);
    setStrategySaving(true);
    try {
      const updated = await patchCampaign(api, campaign.id, { [field]: value });
      setCampaign((prev) => ({ ...prev, ...updated }));
      notifications.show({
        title: 'Strategy setting updated',
        message: `${field} saved.`,
        color: 'sage',
      });
    } catch (err) {
      setCampaign(previousCampaign);
      notifications.show({
        title: 'Could not update strategy setting',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setStrategySaving(false);
    }
  }

  async function patchBookingDays(nextBookingDays) {
    if (!campaign) return;
    const weekdays = nextBookingDays.map((row) => String(row.weekday || '').toLowerCase()).filter(Boolean);
    const hasDuplicates = new Set(weekdays).size !== weekdays.length;
    if (hasDuplicates) {
      notifications.show({
        title: 'Duplicate weekday',
        message: 'Use each weekday at most once in booking days.',
        color: 'red',
      });
      return;
    }
    const invalidHours = nextBookingDays.some((row) => Number(row.endHour) <= Number(row.startHour));
    if (invalidHours) {
      notifications.show({
        title: 'Invalid hours',
        message: 'Each booking day must have endHour greater than startHour.',
        color: 'red',
      });
      return;
    }
    await patchStrategyField('bookingDays', nextBookingDays);
  }

  async function handleFindMore() {
    if (!campaign?.id) return;
    setSourcing(true);
    try {
      const result = await findMoreProspects(api, campaign.id);
      const found = Array.isArray(result?.prospects) ? result.prospects.length : 0;
      // Refresh the campaign so proposedProspects lands in local state, then jump
      // to the review page.
      await load();
      if (found > 0) {
        navigate(`/v2/campaigns/${campaign.id}/review`);
      } else {
        notifications.show({
          title: 'No new candidates found',
          message: 'Apollo returned no new matches this time. Try loosening the targeting or check back later.',
          color: 'yellow',
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Find more failed',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSourcing(false);
    }
  }

  async function handleImport() {
    if (!importFile || !campaign?.id) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importLeadsFromCsv(api, campaign.id, importFile);
      setImportResult(result);
      if (Number(result?.imported || 0) > 0) {
        await load();
      }
    } catch (err) {
      setImportResult({
        imported: 0,
        skipped: 0,
        errors: [{ row: 0, reason: err.response?.data?.error || err.message }],
      });
    } finally {
      setImporting(false);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load this campaign</Text>
          <Text size="sm" c="dimmed">{error}</Text>
          <Anchor component="button" onClick={() => navigate(back.path)}>← Back to {back.label}</Anchor>
        </Stack>
      </Center>
    );
  }

  if (!campaign) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  const isPaused = campaign.paused === true;
  const autonomy = campaign.autonomy || 'manual';
  const assetSelectionMode = campaign.assetSelectionMode === 'manual' ? 'manual' : 'auto';
  const goal = typeof campaign.goal === 'string' ? campaign.goal : '';
  const maxTouches = Number(campaign.maxTouches || DEFAULT_MAX_TOUCHES);
  const perEpisodeBudget = Number(campaign.perEpisodeBudget || DEFAULT_PER_EPISODE_BUDGET);
  const silenceDays = Number(campaign.silenceDays || DEFAULT_SILENCE_DAYS);
  const exploreSharePct = Math.round(Number(campaign.exploreShare ?? DEFAULT_EXPLORE_SHARE) * 100);
  const replyConfidencePct = Math.round(Number(campaign.replyConfidenceThreshold ?? DEFAULT_REPLY_CONFIDENCE_THRESHOLD) * 100);
  const bookingDays = Array.isArray(campaign.bookingDays) ? campaign.bookingDays : [];
  // Archived leads (skipped in approval queue) don't appear in the People table.
  const sortedLeads = [...leads]
    .filter((l) => l.archived !== true)
    .sort((a, b) => {
      const at = new Date(a.lastTouchAt || a.updatedAt || 0).getTime();
      const bt = new Date(b.lastTouchAt || b.updatedAt || 0).getTime();
      return bt - at;
    });

  return (
    <Box style={{ padding: '40px 24px 120px', maxWidth: 1080, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6} align="center">
              <UnstyledButton onClick={() => navigate(back.path)}>
                <Group gap={4} align="center">
                  <IconArrowLeft size={14} style={{ color: 'var(--aviary-muted)' }} />
                  <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{back.label}</Text>
                </Group>
              </UnstyledButton>
              <Text size="sm" style={{ color: 'var(--aviary-disabled)' }}>/</Text>
            </Group>
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 30, lineHeight: 1.2 }}>
              {campaign.name || 'Untitled campaign'}
            </Text>
          </Stack>
          <Group gap="sm">
            <Button
              variant="default"
              color="gray"
              leftSection={<IconUpload size={14} />}
              onClick={() => {
                setImportModalOpen(true);
                setImportResult(null);
                setImportFile(null);
              }}
              disabled={pausing}
            >
              Import list
            </Button>
            <Button
              variant="default"
              color="gray"
              onClick={() => navigate(`/v2?mode=edit&campaignId=${encodeURIComponent(campaign.id)}`)}
              disabled={pausing}
            >
              Edit
            </Button>
            <Button
              variant={isPaused ? 'filled' : 'default'}
              color={isPaused ? 'sage' : 'red'}
              onClick={togglePause}
              loading={pausing}
              disabled={pausing || autonomySaving}
            >
              {isPaused ? 'Resume campaign' : 'Pause campaign'}
            </Button>
          </Group>
        </Group>

        {isPaused && (
          <Paper
            p="md"
            radius="md"
            style={{
              background: 'var(--mantine-color-red-0)',
              border: '1px solid var(--mantine-color-red-3)',
            }}
          >
            <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-red-8)' }}>
              This campaign is paused. No new tasks will be dispatched until resumed.
            </Text>
          </Paper>
        )}

        <Paper p="md" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
          <Stack gap="xs">
            <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Autonomy</Text>
            <SegmentedControl
              value={autonomy}
              onChange={updateAutonomy}
              disabled={autonomySaving || pausing}
              data={[
                { label: 'Manual', value: 'manual' },
                { label: 'Blackbox', value: 'blackbox' },
              ]}
            />
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
              Manual: coach approves every send. Blackbox: Strategist agent approves + sends autonomously.
            </Text>
          </Stack>
        </Paper>

        <Paper p="md" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
          <Stack gap="sm">
            <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Strategy settings</Text>
            {autonomy === 'blackbox' && (
              <Stack gap={4}>
                <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Asset selection</Text>
                <SegmentedControl
                  value={assetSelectionMode}
                  onChange={(value) => patchStrategyField('assetSelectionMode', value)}
                  disabled={strategySaving || pausing || autonomySaving}
                  data={[
                    { label: 'Auto (agent picks)', value: 'auto' },
                    { label: 'Manual (article required)', value: 'manual' },
                  ]}
                />
                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                  Auto uses embedding similarity to pick the best asset for each prospect.
                  Manual requires an article to be set on the campaign.
                </Text>
              </Stack>
            )}
            <Textarea
              label="Goal"
              description="Shown verbatim to the Strategist. Write it like you'd tell a human assistant."
              value={goal}
              maxLength={500}
              autosize
              minRows={3}
              onBlur={(e) => {
                const next = e.currentTarget.value.slice(0, 500);
                if (next !== goal) patchStrategyField('goal', next);
              }}
              onChange={(e) => setCampaign((prev) => ({ ...prev, goal: e.currentTarget.value.slice(0, 500) }))}
              disabled={strategySaving || pausing || autonomySaving}
            />
            <Group grow align="flex-start">
              <NumberInput
                label="Max touches per prospect"
                value={maxTouches}
                min={1}
                max={20}
                onChange={(value) => patchStrategyField('maxTouches', Number(value || DEFAULT_MAX_TOUCHES))}
                disabled={strategySaving || pausing || autonomySaving}
              />
              <NumberInput
                label="Per-episode budget"
                description="How many actions the Strategist can take per prospect before stopping."
                value={perEpisodeBudget}
                min={1}
                max={50}
                onChange={(value) => patchStrategyField('perEpisodeBudget', Number(value || DEFAULT_PER_EPISODE_BUDGET))}
                disabled={strategySaving || pausing || autonomySaving}
              />
            </Group>
            <Group grow align="flex-start">
              <NumberInput
                label="Silence days"
                description="After this many days of no reply, mark the conversation dormant."
                value={silenceDays}
                min={1}
                max={90}
                onChange={(value) => patchStrategyField('silenceDays', Number(value || DEFAULT_SILENCE_DAYS))}
                disabled={strategySaving || pausing || autonomySaving}
              />
              <DateInput
                label="End date"
                description="Campaign auto-closes on this date."
                valueFormat="YYYY-MM-DD"
                value={campaign.endAt ? new Date(campaign.endAt) : null}
                clearable
                onChange={(value) => {
                  if (!value) return patchStrategyField('endAt', null);
                  const y = value.getFullYear();
                  const m = String(value.getMonth() + 1).padStart(2, '0');
                  const d = String(value.getDate()).padStart(2, '0');
                  patchStrategyField('endAt', `${y}-${m}-${d}`);
                }}
                disabled={strategySaving || pausing || autonomySaving}
              />
            </Group>
            <Group grow align="flex-start">
              <NumberInput
                label="Explore share (%)"
                description="Fraction of episodes the Strategist uses to try new rules."
                value={exploreSharePct}
                min={0}
                max={100}
                onChange={(value) => patchStrategyField('exploreShare', Number(value || 0) / 100)}
                disabled={strategySaving || pausing || autonomySaving}
              />
              <NumberInput
                label="Reply confidence threshold (%)"
                description="Minimum confidence before Jev auto-classifies a reply."
                value={replyConfidencePct}
                min={0}
                max={100}
                onChange={(value) => patchStrategyField('replyConfidenceThreshold', Number(value || 0) / 100)}
                disabled={strategySaving || pausing || autonomySaving}
              />
            </Group>
            <Stack gap="xs">
              <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Booking days</Text>
              {bookingDays.map((entry, idx) => (
                <Group key={`${entry.weekday}-${idx}`} grow align="flex-end">
                  <Select
                    label="Weekday"
                    data={VALID_WEEKDAY_OPTIONS}
                    value={entry.weekday}
                    onChange={(value) => {
                      const next = [...bookingDays];
                      next[idx] = { ...next[idx], weekday: value || '' };
                      patchBookingDays(next);
                    }}
                    disabled={strategySaving || pausing || autonomySaving}
                  />
                  <NumberInput
                    label="Start hour"
                    value={entry.startHour}
                    min={0}
                    max={23}
                    onChange={(value) => {
                      const next = [...bookingDays];
                      next[idx] = { ...next[idx], startHour: Number(value || 0) };
                      patchBookingDays(next);
                    }}
                    disabled={strategySaving || pausing || autonomySaving}
                  />
                  <NumberInput
                    label="End hour"
                    value={entry.endHour}
                    min={0}
                    max={23}
                    onChange={(value) => {
                      const next = [...bookingDays];
                      next[idx] = { ...next[idx], endHour: Number(value || 0) };
                      patchBookingDays(next);
                    }}
                    disabled={strategySaving || pausing || autonomySaving}
                  />
                  <Button
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                      const next = bookingDays.filter((_, i) => i !== idx);
                      patchBookingDays(next);
                    }}
                    disabled={strategySaving || pausing || autonomySaving}
                  >
                    Remove
                  </Button>
                </Group>
              ))}
              <Button
                variant="light"
                color="sage"
                onClick={() => {
                  const next = [...bookingDays, { weekday: 'monday', startHour: 9, endHour: 17 }];
                  patchBookingDays(next);
                }}
                disabled={strategySaving || pausing || autonomySaving}
              >
                Add booking day
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Stat cards */}
        <Group gap="md" grow>
          <StatCard label="contacted" value={counts.contacted + counts.clicked + counts.booked} />
          <StatCard label="open" value={openRate} />
          <StatCard label="clicked" value={counts.clicked} />
          <StatCard label="booked" value={counts.booked} />
          <StatCard label="replied" value={counts.replied} />
        </Group>

        {/* Funnel */}
        <Funnel counts={counts} />

        {/* Top-up bar — Find more prospects + pending batch banner */}
        {(() => {
          // Active pool = non-archived, non-lost leads that are still in the funnel.
          // Once a lead books or is lost, they leave the pool → auto top-up refills.
          const activePoolCount = leads.filter((l) => (
            l.archived !== true
            && !['booked', 'closed', 'lost'].includes(String(l.stage || '').toLowerCase())
          )).length;
          const pending = Array.isArray(campaign.proposedProspects) ? campaign.proposedProspects : [];
          const pendingCount = pending.length;
          const strongCount = pending.filter((p) => Number(p.fitScore ?? p.score ?? 0) >= 80).length;
          const caughtUp = activePoolCount >= POOL_TARGET;
          const progressPct = Math.min(100, (activePoolCount / POOL_TARGET) * 100);

          if (pendingCount > 0) {
            // READY state — sage banner + Review & qualify link
            return (
              <Paper
                p="md"
                radius="md"
                style={{
                  background: 'var(--mantine-color-sage-0)',
                  border: '1px solid var(--mantine-color-sage-4)',
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="md">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                      {pendingCount} new {pendingCount === 1 ? 'candidate' : 'candidates'} ready to review
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      found on-demand · {strongCount > 0 ? `${strongCount} look strong` : 'ready for your call'}
                    </Text>
                  </Stack>
                  <Button
                    color="sage"
                    size="sm"
                    onClick={() => navigate(`/v2/campaigns/${campaign.id}/review`)}
                    styles={{ root: { flexShrink: 0 } }}
                  >
                    Review &amp; qualify →
                  </Button>
                </Group>
              </Paper>
            );
          }

          // IDLE or CAUGHT UP — top-up bar with progress + Find more button
          return (
            <Paper p="md" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
              <Stack gap="xs">
                <Group justify="space-between" align="baseline" wrap="wrap">
                  <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
                    Prospect pool ·{' '}
                    <Text component="span" size="sm" style={{ color: 'var(--aviary-muted)' }}>
                      aiming for {POOL_TARGET} active
                    </Text>
                  </Text>
                  <Group gap="sm" align="center">
                    <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                      {activePoolCount} of {POOL_TARGET}
                    </Text>
                    {sourcing ? (
                      <Button size="xs" color="sage" loading disabled>Finding…</Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="light"
                        color="sage"
                        leftSection={<IconPlus size={12} />}
                        onClick={handleFindMore}
                      >
                        Find more prospects
                      </Button>
                    )}
                  </Group>
                </Group>
                <Box
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'var(--aviary-disabled)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    style={{
                      height: '100%',
                      width: `${progressPct}%`,
                      background: caughtUp
                        ? 'var(--mantine-color-sage-6)'
                        : 'var(--mantine-color-sage-4)',
                      transition: 'width 200ms ease',
                    }}
                  />
                </Box>
                <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                  {sourcing
                    ? `Finding people who match your original brief — back in a minute.`
                    : caughtUp
                      ? `Pool full at ${POOL_TARGET}. New prospects will come from replies dropping off.`
                      : `Hit "Find more" to source another batch. De-dups against everyone already in your pipeline.`}
                </Text>
              </Stack>
            </Paper>
          );
        })()}

        {/* Tabs */}
        <Group gap="xs">
          {[
            { key: 'people', label: `People ${sortedLeads.length}` },
            { key: 'messages', label: 'Messages' },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <UnstyledButton
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  border: `1px solid ${active ? 'var(--aviary-ink)' : 'var(--aviary-disabled)'}`,
                  background: active ? 'var(--aviary-ink)' : 'transparent',
                  color: active ? '#fff' : 'var(--aviary-ink)',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {t.label}
              </UnstyledButton>
            );
          })}
        </Group>

        {tab === 'people' && (
          <PeopleTable
            leads={sortedLeads}
            onOpenLead={(id) => navigate(`/v2/leads/${id}`, {
              state: { back: { path: `/v2/campaigns/${campaign.id}`, label: campaign.name || 'Campaign' } },
            })}
          />
        )}
        {tab === 'messages' && <MessagesTab touches={touches} />}

        <Divider mt="xl" />
        <Text size="xs" ta="center" style={{ color: 'var(--aviary-muted)' }}>
          Need to edit cadence, phases, or advanced settings?{' '}
          <Anchor size="xs" href={`/campaigns/${campaign.id}`}>Open v1 campaign builder →</Anchor>
        </Text>

        <Modal
          opened={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          title="Import contacts from CSV"
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Upload a CSV with columns: <strong>name</strong>, <strong>phone</strong> or <strong>mobile</strong> (AU mobile). Optional: company, suburb. Max 500 rows.
            </Text>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                setImportFile(e.target.files?.[0] || null);
                setImportResult(null);
              }}
            />
            {importResult && (
              <Stack gap={4}>
                <Text size="sm" fw={600} c={importResult.imported > 0 ? 'green' : 'dimmed'}>
                  {importResult.imported} imported, {importResult.skipped} skipped
                </Text>
                {(importResult.errors || []).slice(0, 5).map((item) => (
                  <Text key={`${item.row}-${item.reason}`} size="xs" c="red">Row {item.row}: {item.reason}</Text>
                ))}
              </Stack>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setImportModalOpen(false)}>Cancel</Button>
              <Button
                onClick={handleImport}
                loading={importing}
                disabled={!importFile || importing}
              >
                Import
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Box>
  );
}
